import React, { useEffect, useMemo, useRef, useState } from "react";
import { EventLanguage } from "../../../utils/Languages";
import MainComponentWrapper from "../../Common/MainComponentWrapper";

const FormLabal = EventLanguage.EventForm.BasicInFoForm.FormLabal;

const LocationFieldError = ({ message }) =>
  message ? <p className="event-create-field-error">{message}</p> : null;

function buildLocationQuery(form) {
  return [
    form?.address,
    form?.city,
    form?.state,
    form?.postalCode || form?.zip,
    form?.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function buildMapEmbedUrl(locationQuery) {
  if (!locationQuery?.trim()) {
    return "";
  }
  return `https://maps.google.com/maps?q=${encodeURIComponent(
    locationQuery
  )}&hl=en&z=14&output=embed`;
}

const LocationMap = ({
  countries,
  watch,
  event,
  setAddressForm,
  addressForm,
  locationError,
}) => {
  const [mapQuery, setMapQuery] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    const fromWatch = buildLocationQuery({
      address: watch("address"),
      city: watch("city"),
      country: watch("country"),
      state: watch("state"),
      postalCode: watch("postalCode"),
    });
    if (fromWatch) {
      setMapQuery(fromWatch);
    }
  }, [event]);

  useEffect(() => {
    const query = buildLocationQuery(addressForm);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (!query.trim()) {
      setMapQuery("");
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      setMapQuery(query);
    }, 500);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [addressForm]);

  const mapEmbedUrl = useMemo(() => buildMapEmbedUrl(mapQuery), [mapQuery]);

  const handleSearch = () => {
    setMapQuery(buildLocationQuery(addressForm));
  };

  const InputChange = (key, value) => {
    setAddressForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <MainComponentWrapper>
      <div className="event-create-location-layout">
        <div className="event-create-location-fields">
          <div className="event-create-field">
            <label htmlFor="event-location-address">
              {FormLabal.address.label}
            </label>
            <input
              id="event-location-address"
              className="event-create-input"
              type="text"
              value={addressForm?.address || ""}
              onChange={(e) => InputChange("address", e.target.value)}
              placeholder={FormLabal.address.placeHolder}
            />
            <LocationFieldError message={locationError?.address} />
          </div>

          <div className="event-create-fields-grid">
            <div className="event-create-field">
              <label htmlFor="event-location-city">{FormLabal.city.label}</label>
              <input
                id="event-location-city"
                className="event-create-input"
                type="text"
                value={addressForm?.city || ""}
                onChange={(e) => InputChange("city", e.target.value)}
                placeholder={FormLabal.city.placeHolder}
              />
              <LocationFieldError message={locationError?.city} />
            </div>

            <div className="event-create-field">
              <label htmlFor="event-location-state">
                {FormLabal.state.label}
              </label>
              <input
                id="event-location-state"
                className="event-create-input"
                type="text"
                value={addressForm?.state || ""}
                onChange={(e) => InputChange("state", e.target.value)}
                placeholder={FormLabal.state.placeHolder}
              />
              <LocationFieldError message={locationError?.state} />
            </div>
          </div>

          <div className="event-create-fields-grid">
            <div className="event-create-field">
              <label htmlFor="event-location-postal">
                {FormLabal.postalCode.label}
              </label>
              <input
                id="event-location-postal"
                className="event-create-input"
                type="text"
                value={addressForm?.postalCode || ""}
                onChange={(e) => InputChange("postalCode", e.target.value)}
                placeholder={FormLabal.postalCode.placeHolder}
              />
              <LocationFieldError message={locationError?.postalCode} />
            </div>

            <div className="event-create-field">
              <label htmlFor="event-location-country">
                {FormLabal.country.label}
              </label>
              <select
                id="event-location-country"
                className="form-control"
                value={addressForm?.country || ""}
                onChange={(e) => InputChange("country", e.target.value)}
              >
                <option value="">Please select</option>
                {countries?.map((option, index) => (
                  <option key={index} value={option?.name || ""}>
                    {option?.name || ""}
                  </option>
                ))}
              </select>
              <LocationFieldError message={locationError?.country} />
            </div>
          </div>

          <button
            type="button"
            className="event-create-search-btn"
            onClick={handleSearch}
          >
            <i className="bi bi-search" aria-hidden="true" />
            <span>Search</span>
          </button>
        </div>

        <div className="event-create-location-map-wrap">
          {mapEmbedUrl ? (
            <iframe
              className="mapContainer event-create-location-map"
              title="Event location map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={mapEmbedUrl}
            />
          ) : (
            <div
              className="mapContainer event-create-location-map event-create-location-map-empty"
              role="status"
            >
              <i className="bi bi-geo-alt" aria-hidden="true" />
              <span>Fill in the location fields to preview the map</span>
            </div>
          )}
        </div>
      </div>
    </MainComponentWrapper>
  );
};

export default LocationMap;
